import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL; 
const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('cust_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // Silently ignore 429 on all routes except login
    if (error.response?.status === 429) {
      const isLogin = error.config?.url?.includes('/auth/login');
      if (!isLogin) return Promise.resolve(null);
    }
    let message = error.response?.data?.message || error.message || 'Something went wrong';
    if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
      message = error.response.data.errors.map(err => err.message).join(', ');
    }
    if (error.response?.status === 401) {
      localStorage.removeItem('cust_token');
      localStorage.removeItem('cust_user');
    }
    return Promise.reject(new Error(message));
  }
);

export default api;

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (email, clientUrl) => api.post('/auth/forgot-password', { email, clientUrl }),
  resetPassword: (token, data) => api.patch(`/auth/reset-password/${token}`, data),
  verifyEmail: (token) => api.get(`/auth/verify-email/${token}`),
};

export const supermarketAPI = {
  getAll: (params) => api.get('/supermarkets/public', { params }),
  getById: (id) => api.get(`/supermarkets/public/${id}`),
  requestJoin: (data) => api.post('/supermarkets/public/request-join', data),
};

export const productAPI = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
};

export const categoryAPI = {
  getAll: (params) => api.get('/categories', { params }),
};

export const branchAPI = {
  getPublic: (supermarketId) => api.get('/branches/public', { params: { supermarket: supermarketId } }),
};

export const pickupAPI = {
  getAvailable: (params) => api.get('/pickups/available', { params }),
};

export const deliveryAPI = {
  getAvailability: () => api.get('/delivery/availability'),
  getQuote: (data) => api.post('/delivery/quote', data),
};


export const orderAPI = {
  create: (data) => api.post('/orders', data),
  getMyOrders: (params) => api.get('/orders/my', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  cancel: (id, reason) => api.patch(`/orders/${id}/cancel`, { reason }),
  ratePickupExperience: (id, data) => api.post(`/orders/${id}/rate-pickup`, data),
};

export const paymentAPI = {
  initialize: (data) => api.post('/payments/initialize', data),
  verify: (data) => api.post('/payments/verify', data),
  walletPay: (data) => api.post('/payments/wallet-pay', data),
};

export const reviewAPI = {
  create: (data) => api.post('/reviews', data),
  getAll: (params) => api.get('/reviews', { params }),
};

export const userAPI = {
  updateProfile: (data) => api.patch('/users/me', data),
  updateAvatar: (formData) => api.patch('/users/me/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Supermarket favorites
  addFavoriteSupermarket: (id) => api.post(`/users/me/favorites/${id}`),
  removeFavoriteSupermarket: (id) => api.delete(`/users/me/favorites/${id}`),
  // Product favorites
  addFavoriteProduct: (id) => api.post(`/users/me/favorite-products/${id}`),
  removeFavoriteProduct: (id) => api.delete(`/users/me/favorite-products/${id}`),
};

export const notificationAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/mark-all-read'),
};

export const announcementAPI = {
  getActive: (params) => api.get('/announcements/active', { params }),
};
